import React, { FocusEvent, KeyboardEvent } from 'react';
import styled, { css } from 'styled-components';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import { Portal } from '@radix-ui/react-portal';

// constants
import { useCombobox } from 'downshift';
import { usePrevious } from '@gloat/hooks';
import { colors } from '../../style';

// hooks

// components
import DotsLoader from '../../components/dots-loader';
import { SearchInput } from '../../components/input';

// types
import { InputProps } from '../../components/input/input';
import Icon from '../../components/icon';
import { Flex } from '../../components/box';

// autocomplete types
export type AutocompleteItem = { name: string } | string;
export type Value = string | undefined;
export type AutoCompleteOnChangeEvent = (
  e: React.ChangeEvent<HTMLInputElement> | React.MouseEvent<HTMLElement>,
  value?: string,
) => void;

type InputChangEvent = React.ChangeEvent<HTMLInputElement>;

// `waitFor` default timeout is 1000 which makes tests of `Autocomplete` flaky, so let's
// reduce this timeout on testing environment to remove flakiness and speed up tests
export const autocompleteTypingTimeout = import.meta.env.VITEST ? 200 : 1000;

export const dropdownSizes = {
  sm: {
    height: '120px',
  },
  md: {
    height: '180px',
  },
  xl: {
    height: '300px',
  },
};

export type AutocompleteProps<T = AutocompleteItem> = {
  value?: string;
  displayValue?: string;
  style?: React.CSSProperties;
  focusOnMount?: boolean;
  dropdownHeight?: keyof typeof dropdownSizes;
  onFocus?: (e: FocusEvent<any>) => void;
  onChange?: AutoCompleteOnChangeEvent;
  onBlur?: (e?: FocusEvent<any>) => void;
  makeItemFromInputValue?: (value: Value) => any;
  blurAfterEnterClicked?: boolean;
  onBackSpaceClickWhenInputIsEmpty?: (event: KeyboardEvent<HTMLInputElement> | KeyboardEvent<any> | undefined) => void;
  onBackspace?: () => void;
  onSelect?: (item: T) => void;
  clearOnSelect?: boolean;
  keepFocusAfterSelect?: boolean;
  getInputRef?: (input: HTMLInputElement) => HTMLInputElement;
  inputProps?: InputProps;
  disableClear?: boolean;
  items?: any[];
  filterItems?: (items?: T[]) => T[];
  'data-name'?: string;
  isFetching?: boolean;
  block?: boolean;
  onClear?: () => void;
  className?: string;
  'aria-required'?: string;
  hasFetched?: boolean;
  renderItem?: (item: T) => React.ReactNode;
  input?: React.ReactElement;
  disabled?: boolean;
  allowOpenValue?: boolean;
  id?: string | number;
  allowOpenValueAsItem?: boolean;
  shouldScrollToView?: boolean;
  placeholder?: string;
  itemToString?: (item: T | null) => string;
  isAsync?: boolean;
  onEnter?: () => void;
  /** default to true. if false we will not call onClear when the input is cleared via the backspace keyboard key */
  callOnClearOnBackspaceWhenInputIsEmpty?: boolean;
  /**
   * When true, renders the dropdown in a portal.
   * Useful when the Autocomplete is inside a container with overflow hidden or z-index issues.
   * Auto-detects modal containers to portal within the modal's stacking context.
   * @default false
   */
  shouldRenderInPortal?: boolean;
};

type ErrorBoundaryState = {
  hasError: boolean;
};

class AutoCompleteErrorBoundary extends React.Component<{ children: React.ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Dropdown Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Flex data-testid={'dropdown-error'} justifyContent={'center'} padding={'xs'}>
          <Icon name={'EXCLAMATION'} variant={'negative'} />
        </Flex>
      );
    }

    return <>{this.props.children}</>;
  }
}

const Autocomplete = <T = AutocompleteItem,>({
  input = <SearchInput />,
  isFetching,
  renderItem = item => {
    if (typeof item === 'string') {
      return item;
    }

    // ignoring ts to support legacy where name is the default property on object we use when items are objects.
    // this is used in too many places and should be handled in the future
    //@ts-expect-error
    return item?.name || '';
  },
  hasFetched,
  items: itemsProp = [],
  makeItemFromInputValue = value => value,
  allowOpenValueAsItem,
  shouldScrollToView,
  value: propValue,
  filterItems = (items?: T[]) => items || [],
  onChange,
  onSelect,
  allowOpenValue,
  callOnClearOnBackspaceWhenInputIsEmpty = true,
  itemToString = item => {
    if (typeof item === 'string') {
      return item;
    }

    // ignoring ts to support legacy where name is the default property on object we use when items are objects.
    //@ts-expect-error
    return item?.name || '';
  },
  placeholder,
  id,
  displayValue,
  disabled,
  block,
  className,
  'data-name': dataName,
  onFocus,
  onBlur,
  inputProps = {},
  clearOnSelect,
  onBackspace,
  onBackSpaceClickWhenInputIsEmpty,
  focusOnMount,
  keepFocusAfterSelect,
  onClear: onClearProp,
  isAsync = true,
  dropdownHeight,
  'aria-required': isRequired,
  onEnter,
  shouldRenderInPortal = false,
}: AutocompleteProps<T>) => {
  const [isTyping, setIsTyping] = React.useState(false);

  const onTyping = React.useRef(
    debounce(function () {
      // continually delays setting "isTyping" to false for 1000ms until the user has stopped typing and the delay runs out
      setIsTyping(false);
    }, autocompleteTypingTimeout),
  );

  // Clean up the debounced function when the component unmounts
  React.useEffect(() => {
    return () => {
      onTyping.current.cancel();
    };
  }, []);

  /**
   * Input value state
   */
  const [inputValue, setInputValue] = React.useState(displayValue || propValue);

  const prevDisplayValue = usePrevious(displayValue);
  React.useEffect(() => {
    if (displayValue !== prevDisplayValue) {
      setInputValue(displayValue);
    }
  }, [displayValue]);

  const prevValue = usePrevious(propValue);
  React.useEffect(() => {
    if (propValue !== prevValue) {
      setInputValue(propValue);
    }
  }, [propValue]);

  /**
   * Items
   */
  const itemsWithoutItemsParentDoesntWantToDisplay = filterItems ? filterItems(itemsProp) : itemsProp || [];
  const openValueItemToAddToItems = makeItemFromInputValue?.(inputValue);
  const isOpenValueAlreadyInItems =
    itemsWithoutItemsParentDoesntWantToDisplay.find(
      item => item?.name?.toLowerCase?.() === inputValue?.toLowerCase?.(),
    ) ||
    itemsWithoutItemsParentDoesntWantToDisplay.find(
      (item: string) => item?.toLowerCase?.() === inputValue?.toLowerCase?.(),
    );

  // if the component is not async - we treat it has if it always has fetched
  const isFetched = hasFetched || !isAsync;

  const shouldAddValueToItems = Boolean(allowOpenValueAsItem && isFetched && !isOpenValueAlreadyInItems && inputValue);
  const allItems = shouldAddValueToItems
    ? [openValueItemToAddToItems, ...itemsWithoutItemsParentDoesntWantToDisplay]
    : itemsWithoutItemsParentDoesntWantToDisplay;

  const validItems = allItems.filter(Boolean);
  const itemsToRender = isAsync
    ? // if is async, we dont allow input filtering, changing the input will trigger a new fetch request by the parent
      validItems
    : // if not in async mode, we want to filter from the predefined items list according to the input value
      validItems.filter(item =>
        isAsync
          ? true
          : itemToString(item)
              ?.toLowerCase?.()
              .includes(inputValue?.toLowerCase?.() || ''),
      );

  /**
   * Refs
   */
  const ref = React.useRef<HTMLInputElement>(null);
  const inputWrapperRef = React.useRef<HTMLDivElement>(null);
  const [portalContainer, setPortalContainer] = React.useState<HTMLElement | undefined>(undefined);

  const setInputWrapperRef = (node: HTMLDivElement | null) => {
    inputWrapperRef.current = node;
    if (!shouldRenderInPortal || !node) {
      return;
    }
    const modalContainer = node.closest('[data-slot="modal-content"]');
    setPortalContainer(modalContainer instanceof HTMLElement ? modalContainer : undefined);
  };

  React.useEffect(() => {
    if (focusOnMount && ref.current) {
      ref.current.focus();
    }
  }, [focusOnMount]);

  /**
   * Downshift props
   */
  const { isOpen, getMenuProps, getInputProps, highlightedIndex, getItemProps, selectItem } = useCombobox<T>({
    items: itemsToRender,
    itemToString,
    inputValue,
    onStateChange: ({ type, selectedItem }) => {
      switch (type) {
        case useCombobox.stateChangeTypes.FunctionSelectItem:
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          if (selectedItem) {
            setInputValue(clearOnSelect ? '' : itemToString(selectedItem));
            onSelect?.(selectedItem);
            selectItem(null as T);

            if (keepFocusAfterSelect && ref.current) {
              ref.current.focus();
            }
          }

          break;
        default:
          break;
      }
    },
    stateReducer: (state, actionAndChanges) => {
      const { type, changes } = actionAndChanges;
      switch (type) {
        case useCombobox.stateChangeTypes.FunctionSelectItem:
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick: {
          return {
            ...changes,
            isOpen: false,
          };
        }

        default:
          return changes;
      }
    },
  });

  /**
   * Methods
   */
  const onClear = () => {
    onClearProp?.();
    selectItem('' as T);
    setInputValue('');
  };

  // when allowOpenValueAsItem is true and the autocomplete is async,
  // we want to hide the menu when the user is typing. so the user wont see the value changes while typing,
  // only after we re-fetch the items.
  const shouldHideMenuWhileTyping = isAsync && allowOpenValueAsItem && isTyping;
  const isMenuOpen = isOpen && !shouldHideMenuWhileTyping;

  React.useEffect(() => {
    // the purpose of this effect is to scroll the last item into view when the menu is open
    if (shouldScrollToView && isMenuOpen && itemsToRender?.length) {
      const menuItems = document?.getElementsByClassName('menu-item') || [];

      if (menuItems.length) {
        document.getElementsByClassName('menu-item')[menuItems.length - 1]?.scrollIntoView?.({ block: 'end' });
      }
    }
  }, [itemsToRender?.length]);

  // When portaled, the menu loses its CSS positioning context — calculate position from the input's rect relative to the portal container
  const [portalMenuStyle, setPortalMenuStyle] = React.useState<React.CSSProperties | undefined>(undefined);

  React.useLayoutEffect(() => {
    if (!isOpen || !shouldRenderInPortal) {
      return;
    }
    const computeStyle = (): React.CSSProperties | undefined => {
      const inputRect = inputWrapperRef.current?.getBoundingClientRect();
      if (!inputRect) {
        return undefined;
      }
      if (portalContainer) {
        const containerRect = portalContainer.getBoundingClientRect();
        return {
          position: 'absolute',
          top: inputRect.bottom - containerRect.top + portalContainer.scrollTop + 10,
          left: inputRect.left - containerRect.left + portalContainer.scrollLeft,
          width: inputRect.width,
        };
      }
      return {
        position: 'absolute',
        top: inputRect.bottom + window.scrollY + 10,
        left: inputRect.left + window.scrollX,
        width: inputRect.width,
      };
    };

    setPortalMenuStyle(computeStyle());

    const handler = () => setPortalMenuStyle(computeStyle());
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, true);
    };
  }, [isOpen, shouldRenderInPortal, portalContainer]);

  const menu = (
    <ul className={classNames('menu', { 'menu-open': isMenuOpen })} {...getMenuProps({ 'aria-labelledby': undefined })}>
      {isFetching && isMenuOpen ? (
        <div className={'loader-container'}>
          <DotsLoader size={'sm'} />
        </div>
      ) : (
        <AutoCompleteErrorBoundary>
          {isMenuOpen &&
            itemsToRender.map((item, index) => {
              const isCreatableItem = shouldAddValueToItems && index === 0;
              return (
                <li
                  className={classNames('menu-item', { active: highlightedIndex === index })}
                  key={`${index}`}
                  {...getItemProps({ item, index })}
                >
                  {isCreatableItem ? `"${item.name ?? itemToString(item)}"` : renderItem(item)}
                </li>
              );
            })}
        </AutoCompleteErrorBoundary>
      )}
    </ul>
  );

  return (
    <AutoCompleteWrapper
      data-name={dataName}
      className={classNames('box-autocomplete', className, { block })}
      dropdownHeight={dropdownHeight}
    >
      <div ref={setInputWrapperRef} aria-label={inputProps?.ariaLabel || placeholder || inputProps?.placeholder}>
        {React.cloneElement(input, {
          'aria-required': isRequired,
          disabled,
          //@ts-expect-error
          onClear,
          ...inputProps,
          // Downshift's getInputProps merges this ref via useComposedRef during commit, not render — safe to forward.
          // eslint-disable-next-line react-hooks/refs
          ...getInputProps({
            ref,
            id: id ? `${id}` : '',
            autoComplete: 'off',
            placeholder: placeholder || inputProps?.placeholder,
            onChange: (e: InputChangEvent) => {
              onChange?.(e, e.target.value);

              setInputValue(e.target.value);

              setIsTyping(true);
              onTyping.current();

              if (!e.target.value && callOnClearOnBackspaceWhenInputIsEmpty) {
                onClear();
              }
            },
            onKeyDown: e => {
              if (e.key === 'Enter' && highlightedIndex === -1 && allowOpenValue && inputValue) {
                e.preventDefault();
                selectItem(makeItemFromInputValue(inputValue));
              }
              if (e.key === 'Backspace') {
                onBackspace?.();
                if (!inputValue) {
                  onBackSpaceClickWhenInputIsEmpty?.(e);
                }
              }

              if (e.key === 'Enter') {
                onEnter?.();
              }
            },
            onBlur,
            onFocus,
            ...(input?.props || {}),
          }),
        })}
      </div>
      {shouldRenderInPortal ? (
        <Portal container={portalContainer}>
          <PortalMenuWrapper dropdownHeight={dropdownHeight} style={portalMenuStyle}>
            {menu}
          </PortalMenuWrapper>
        </Portal>
      ) : (
        menu
      )}
    </AutoCompleteWrapper>
  );
};

type MenuStyleProps = {
  dropdownHeight: AutocompleteProps['dropdownHeight'];
};

const menuStyles = css<MenuStyleProps>`
  .menu {
    box-shadow: 0 6px 17px -7px rgba(0, 0, 0, 0.2);
    background: #fff;
    border-radius: 4px;
    z-index: 1000;
    max-height: ${props => dropdownSizes[props.dropdownHeight || 'md']?.height};
    overflow-y: auto;
    padding-inline-start: 0;
    margin: 8px 0 0 0;
    scroll-behavior: auto;
  }

  .menu-item {
    padding: 8px;
    margin: 0 8px;
    font-weight: 500;
    font-size: 1rem;
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;

    &:first-of-type {
      margin-top: 8px;
    }

    &:last-of-type {
      margin-bottom: 8px;
    }

    &.active {
      background: ${colors.lightBlue};
    }
  }

  .loader-container {
    display: flex;
    justify-content: center;
    padding: 8px;
  }
`;

const AutoCompleteWrapper = styled.div<MenuStyleProps>`
  position: relative;

  &.block {
    width: 100%;
  }

  ${menuStyles}

  .menu {
    width: 100%;
    position: absolute;
    left: 0;
  }
`;

const PortalMenuWrapper = styled.div<MenuStyleProps>`
  z-index: 1001;

  ${menuStyles}

  .menu {
    width: 100%;
    margin: 0;
  }
`;

export default Autocomplete;
